import { Point } from './types'
import { MapSettings } from '../../../types'

const SNAP_ANGLE = 15 // Snap every 15 degrees

export const getAngleBetweenPoints = (start: Point, end: Point): number => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const angleRad = Math.atan2(dy, dx)
  const angleDeg = (angleRad * 180) / Math.PI
  return angleDeg
}

export const getDistanceBetweenPoints = (start: Point, end: Point): number => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  return Math.sqrt(dx * dx + dy * dy)
}

export const snapPointToAngle = (
  start: Point,
  current: Point,
  snapAngle: number = SNAP_ANGLE,
): Point => {
  const distance = getDistanceBetweenPoints(start, current)
  const angle = getAngleBetweenPoints(start, current)

  // Round to nearest snap angle
  const snappedAngle = Math.round(angle / snapAngle) * snapAngle
  const angleRad = (snappedAngle * Math.PI) / 180

  return {
    x: start.x + distance * Math.cos(angleRad),
    y: start.y + distance * Math.sin(angleRad),
  }
}

export const formatAngle = (angle: number): string => {
  // Normalize angle to 0-360 range
  let normalized = angle % 360
  if (normalized < 0) normalized += 360

  // Convert to more intuitive angles (0° = right, 90° = down, etc.)
  return `${Math.round(normalized)}°`
}

export const formatDistance = (distance: number): string => {
  return `${Math.round(distance)}px`
}

export const formatRealDistance = (
  pixelDistance: number,
  mapSettings: MapSettings,
): string => {
  const realDistance = pixelDistance / mapSettings.scale
  const unit = mapSettings.units === 'feet' ? 'ft' : 'm'

  if (unit === 'ft') {
    const feet = Math.floor(realDistance)
    const inches = Math.round((realDistance - feet) * 12)
    if (inches === 0) {
      return `${feet}'`
    } else if (feet === 0) {
      return `${inches}"`
    } else {
      return `${feet}' ${inches}"`
    }
  } else {
    // Meters
    if (realDistance < 1) {
      return `${Math.round(realDistance * 100)}cm`
    } else {
      return `${realDistance.toFixed(2)}m`
    }
  }
}

// Draw drag feedback flyout with angle and distance
export const drawDragFeedback = (
  ctx: CanvasRenderingContext2D,
  pivotPoint: Point,
  dragPoint: Point,
  mapSettings: MapSettings,
  isSnapping: boolean = false,
) => {
  const angle = getAngleBetweenPoints(pivotPoint, dragPoint)
  const distance = getDistanceBetweenPoints(pivotPoint, dragPoint)
  const realDistance = formatRealDistance(distance, mapSettings)

  // Save context
  ctx.save()

  // Draw angle arc at pivot point
  const arcRadius = 30
  ctx.strokeStyle = 'rgba(0, 123, 255, 0.4)'
  ctx.fillStyle = 'rgba(0, 123, 255, 0.1)'
  ctx.lineWidth = 2

  // Draw angle sector (pie slice)
  ctx.beginPath()
  ctx.moveTo(pivotPoint.x, pivotPoint.y)
  ctx.lineTo(pivotPoint.x + arcRadius, pivotPoint.y) // 0 degree line
  ctx.arc(
    pivotPoint.x,
    pivotPoint.y,
    arcRadius,
    0,
    (angle * Math.PI) / 180,
    angle < 0
  )
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // Draw reference line at 0 degrees
  ctx.strokeStyle = 'rgba(0, 123, 255, 0.3)'
  ctx.lineWidth = 1
  ctx.setLineDash([3, 3])
  ctx.beginPath()
  ctx.moveTo(pivotPoint.x, pivotPoint.y)
  ctx.lineTo(pivotPoint.x + arcRadius * 1.5, pivotPoint.y)
  ctx.stroke()
  ctx.setLineDash([])

  // Draw snap indicator guides when snapping
  if (isSnapping) {
    // Draw faint guide lines for major angles
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.1)'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 5])

    // Draw guides at 15° intervals
    for (let guideAngle = 0; guideAngle < 360; guideAngle += 15) {
      const angleRad = (guideAngle * Math.PI) / 180
      const guideLength = 500 // Medium guide lines
      ctx.beginPath()
      ctx.moveTo(pivotPoint.x, pivotPoint.y)
      ctx.lineTo(
        pivotPoint.x + guideLength * Math.cos(angleRad),
        pivotPoint.y + guideLength * Math.sin(angleRad),
      )
      ctx.stroke()
    }
    ctx.setLineDash([])
  }

  // Draw flyout box near drag point
  const flyoutPadding = 8
  const flyoutHeight = isSnapping ? 65 : 50
  const flyoutWidth = 120
  const flyoutOffset = 15

  // Position flyout to not overlap with the drag point
  let flyoutX = dragPoint.x + flyoutOffset
  let flyoutY = dragPoint.y - flyoutHeight / 2

  // Adjust if too close to edge (simple bounds check)
  if (flyoutX + flyoutWidth > ctx.canvas.width - 20) {
    flyoutX = dragPoint.x - flyoutWidth - flyoutOffset
  }

  // Draw flyout background
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
  ctx.strokeStyle = '#007BFF'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.roundRect(flyoutX, flyoutY, flyoutWidth, flyoutHeight, 4)
  ctx.fill()
  ctx.stroke()

  // Draw flyout text
  ctx.fillStyle = '#333'
  ctx.font = 'bold 12px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'

  // Angle text
  const angleText = formatAngle(angle)
  if (!isSnapping) {
    ctx.fillText(`Angle: ${angleText}`, flyoutX + flyoutPadding, flyoutY + flyoutHeight * 0.33)
    ctx.fillText(`Distance: ${realDistance}`, flyoutX + flyoutPadding, flyoutY + flyoutHeight * 0.67)
  } else {
    // When snapping, show additional info
    ctx.fillText(`Angle: ${angleText}`, flyoutX + flyoutPadding, flyoutY + flyoutHeight * 0.25)
    ctx.fillText(`Distance: ${realDistance}`, flyoutX + flyoutPadding, flyoutY + flyoutHeight * 0.5)

    // Show snapping indicator
    ctx.fillStyle = '#007BFF'
    ctx.font = '10px system-ui, -apple-system, sans-serif'
    ctx.fillText('🔒 SNAPPING (15°)', flyoutX + flyoutPadding, flyoutY + flyoutHeight * 0.75)
  }

  ctx.restore()
}

// Draw angle indicator and guide lines
export const drawAngleGuides = (
  ctx: CanvasRenderingContext2D,
  start: Point,
  end: Point,
  isSnapping: boolean,
  mapSettings: MapSettings,
) => {
  const angle = getAngleBetweenPoints(start, end)
  const distance = getDistanceBetweenPoints(start, end)

  // Save context state
  ctx.save()

  // Draw guide line from start to end
  if (isSnapping) {
    // Draw faint guide lines for major angles
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.1)'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 5])

    // Draw guides at 0°, 45°, 90°, etc.
    const majorAngles = [0, 45, 90, 135, 180, 225, 270, 315]
    majorAngles.forEach(guideAngle => {
      const angleRad = (guideAngle * Math.PI) / 180
      const guideLength = 1000 // Long guide lines
      ctx.beginPath()
      ctx.moveTo(start.x, start.y)
      ctx.lineTo(
        start.x + guideLength * Math.cos(angleRad),
        start.y + guideLength * Math.sin(angleRad),
      )
      ctx.stroke()
    })

    ctx.setLineDash([])
  }

  // Draw angle indicator near cursor
  const labelOffset = 20
  const labelX = end.x + labelOffset
  const labelY = end.y - labelOffset

  // Background for better readability with border
  ctx.fillStyle = 'rgba(255, 255, 255, 0.98)'
  ctx.strokeStyle = 'rgba(0, 123, 255, 0.8)'
  ctx.lineWidth = 1
  ctx.fillRect(labelX - 8, labelY - 18, 110, 45)
  ctx.strokeRect(labelX - 8, labelY - 18, 110, 45)

  // Draw angle text
  ctx.fillStyle = '#333'
  ctx.font = 'bold 14px system-ui, -apple-system, sans-serif'
  ctx.fillText(formatAngle(angle), labelX, labelY)

  // Draw real-world measurement (primary focus)
  ctx.fillStyle = '#007bff'
  ctx.font = 'bold 16px system-ui, -apple-system, sans-serif'
  ctx.fillText(formatRealDistance(distance, mapSettings), labelX, labelY + 20)

  // Draw small arc to show angle
  if (distance > 30) {
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.5)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(start.x, start.y, 30, 0, (angle * Math.PI) / 180)
    ctx.stroke()
  }

  // Restore context state
  ctx.restore()
}
